import React, { useState, useEffect, useCallback } from 'react'
import { shareService } from '../../../services/shareService'
import { SharePermission, SharedMapAccess } from '../../../types/share'
import {
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelBody,
  TabList,
  Tab,
  TabContent,
  Section,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  PermissionCheckboxes,
  PermissionLabel,
  Button,
  ShareLinkContainer,
  ShareLinkBox,
  ShareLink,
  CopyButton,
  SharesList,
  ShareItem,
  ShareItemHeader,
  UserInfo,
  UserEmail,
  SharedDate,
  PermissionBadges,
  PermissionBadge,
  RevokeButton,
  ErrorMessage,
  SuccessMessage,
  EmptyState,
  Divider,
} from './styled'

interface SharePanelProps {
  mapId: string
  mapName: string
  isOpen: boolean
  onClose: () => void
}

export const SharePanel: React.FC<SharePanelProps> = ({
  mapId,
  mapName,
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'link' | 'direct' | 'manage'>(
    'manage',
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Share link state
  const [linkPermissions, setLinkPermissions] = useState<SharePermission[]>([
    'view',
  ])
  const [expiresIn, setExpiresIn] = useState<string>('24')
  const [maxUses, setMaxUses] = useState<string>('')
  const [shareUrl, setShareUrl] = useState<string | null>(null)

  // Direct share state
  const [targetEmail, setTargetEmail] = useState('')
  const [directPermissions, setDirectPermissions] = useState<SharePermission[]>(
    ['view'],
  )

  // Manage shares state
  const [shares, setShares] = useState<SharedMapAccess[]>([])

  useEffect(() => {
    // Clear messages after 5 seconds
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null)
        setSuccess(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, success])

  const loadShares = useCallback(async () => {
    try {
      const mapShares = await shareService.getMapShares(mapId)
      setShares(mapShares)
    } catch (err) {
      console.error('Failed to load shares:', err)
    }
  }, [mapId])

  const handleCreateShareLink = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await shareService.createShareLink({
        mapId,
        permissions: linkPermissions,
        expiresIn: expiresIn ? parseInt(expiresIn) : undefined,
        maxUses: maxUses ? parseInt(maxUses) : undefined,
      })

      setShareUrl(response.shareUrl)
      setSuccess('Share link created!')

      // Reset form
      setExpiresIn('24')
      setMaxUses('')
    } catch (err) {
      setError('Failed to create share link')
    } finally {
      setLoading(false)
    }
  }

  const handleDirectShare = async () => {
    if (!targetEmail) {
      setError('Please enter an email address')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      await shareService.shareMapDirectly({
        mapId,
        targetUserEmail: targetEmail,
        permissions: directPermissions,
      })

      setSuccess(`Shared with ${targetEmail}`)
      setTargetEmail('')
      await loadShares()
    } catch (err) {
      setError('Failed to share map')
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeShare = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Remove ${userEmail}'s access to this map?`)) {
      return
    }

    try {
      await shareService.revokeShare(mapId, userId)
      await loadShares()
      setSuccess('Access revoked')
    } catch (err) {
      setError('Failed to revoke access')
    }
  }

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl)
      setSuccess('Link copied!')
    }
  }

  const togglePermission = (
    permission: SharePermission,
    current: SharePermission[],
    setter: (permissions: SharePermission[]) => void,
  ) => {
    if (
      permission === 'view' &&
      current.includes('view') &&
      current.length === 1
    ) {
      // Don't allow removing the last permission
      return
    }

    if (current.includes(permission)) {
      setter(current.filter(p => p !== permission))
    } else {
      setter([...current, permission])
    }
  }

  const formatShareDate = (date: Date) => {
    const shareDate = new Date(date)
    const now = new Date()
    const diffInHours = (now.getTime() - shareDate.getTime()) / (1000 * 60 * 60)

    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hours ago`
    } else if (diffInHours < 168) {
      // 7 days
      return `${Math.floor(diffInHours / 24)} days ago`
    } else {
      return shareDate.toLocaleDateString()
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadShares()
    }
  }, [isOpen, loadShares, mapId])

  return (
    <PanelContainer $isOpen={isOpen}>
      <PanelHeader>
        <PanelTitle>Share Settings</PanelTitle>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </PanelHeader>

      <PanelBody>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <TabList>
          <Tab
            $active={activeTab === 'manage'}
            onClick={() => setActiveTab('manage')}>
            Shared With
          </Tab>
          <Tab
            $active={activeTab === 'direct'}
            onClick={() => setActiveTab('direct')}>
            Add People
          </Tab>
          <Tab
            $active={activeTab === 'link'}
            onClick={() => setActiveTab('link')}>
            Get Link
          </Tab>
        </TabList>

        <TabContent>
          {activeTab === 'manage' && (
            <Section>
              <SectionTitle>People with access</SectionTitle>
              {shares.length === 0 ? (
                <EmptyState>
                  <p>This map hasn't been shared yet</p>
                  <p>Share it with others to collaborate</p>
                </EmptyState>
              ) : (
                <SharesList>
                  {shares.map(share => (
                    <ShareItem key={share.id}>
                      <ShareItemHeader>
                        <UserInfo>
                          <UserEmail>{share.userId}</UserEmail>
                          <SharedDate>
                            Shared {formatShareDate(share.sharedAt)}
                          </SharedDate>
                        </UserInfo>
                        <RevokeButton
                          onClick={() =>
                            handleRevokeShare(share.userId, share.userId)
                          }
                          title="Remove access">
                          Remove
                        </RevokeButton>
                      </ShareItemHeader>
                      <PermissionBadges>
                        {share.permissions.map(perm => (
                          <PermissionBadge key={perm} $type={perm}>
                            {perm === 'view' && '👁 Can view'}
                            {perm === 'edit' && '✏️ Can edit'}
                            {perm === 'comment' && '💬 Can comment'}
                          </PermissionBadge>
                        ))}
                      </PermissionBadges>
                    </ShareItem>
                  ))}
                </SharesList>
              )}
            </Section>
          )}

          {activeTab === 'direct' && (
            <Section>
              <SectionTitle>Share with specific people</SectionTitle>
              <FormGroup>
                <Label>Email address</Label>
                <Input
                  type="email"
                  value={targetEmail}
                  onChange={e => setTargetEmail(e.target.value)}
                  placeholder="user@example.com"
                />
              </FormGroup>

              <FormGroup>
                <Label>Permissions</Label>
                <PermissionCheckboxes>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={directPermissions.includes('view')}
                      onChange={() =>
                        togglePermission(
                          'view',
                          directPermissions,
                          setDirectPermissions,
                        )
                      }
                    />
                    <span>Can view</span>
                  </PermissionLabel>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={directPermissions.includes('edit')}
                      onChange={() =>
                        togglePermission(
                          'edit',
                          directPermissions,
                          setDirectPermissions,
                        )
                      }
                    />
                    <span>Can edit</span>
                  </PermissionLabel>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={directPermissions.includes('comment')}
                      onChange={() =>
                        togglePermission(
                          'comment',
                          directPermissions,
                          setDirectPermissions,
                        )
                      }
                    />
                    <span>Can comment</span>
                  </PermissionLabel>
                </PermissionCheckboxes>
              </FormGroup>

              <Button
                onClick={handleDirectShare}
                disabled={loading || !targetEmail}>
                {loading ? 'Sharing...' : 'Share'}
              </Button>
            </Section>
          )}

          {activeTab === 'link' && (
            <Section>
              <SectionTitle>Create shareable link</SectionTitle>

              <FormGroup>
                <Label>Permissions</Label>
                <PermissionCheckboxes>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={linkPermissions.includes('view')}
                      onChange={() =>
                        togglePermission(
                          'view',
                          linkPermissions,
                          setLinkPermissions,
                        )
                      }
                    />
                    <span>Can view</span>
                  </PermissionLabel>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={linkPermissions.includes('edit')}
                      onChange={() =>
                        togglePermission(
                          'edit',
                          linkPermissions,
                          setLinkPermissions,
                        )
                      }
                    />
                    <span>Can edit</span>
                  </PermissionLabel>
                  <PermissionLabel>
                    <input
                      type="checkbox"
                      checked={linkPermissions.includes('comment')}
                      onChange={() =>
                        togglePermission(
                          'comment',
                          linkPermissions,
                          setLinkPermissions,
                        )
                      }
                    />
                    <span>Can comment</span>
                  </PermissionLabel>
                </PermissionCheckboxes>
              </FormGroup>

              <FormGroup>
                <Label>Link expires in (hours)</Label>
                <Input
                  type="number"
                  value={expiresIn}
                  onChange={e => setExpiresIn(e.target.value)}
                  placeholder="24"
                  min="1"
                />
              </FormGroup>

              <FormGroup>
                <Label>Maximum uses (optional)</Label>
                <Input
                  type="number"
                  value={maxUses}
                  onChange={e => setMaxUses(e.target.value)}
                  placeholder="Unlimited"
                  min="1"
                />
              </FormGroup>

              <Button
                onClick={handleCreateShareLink}
                disabled={loading || linkPermissions.length === 0}>
                {loading ? 'Creating...' : 'Create Link'}
              </Button>

              {shareUrl && (
                <>
                  <Divider />
                  <ShareLinkContainer>
                    <Label>Share this link:</Label>
                    <ShareLinkBox>
                      <ShareLink>{shareUrl}</ShareLink>
                      <CopyButton onClick={handleCopyLink}>Copy</CopyButton>
                    </ShareLinkBox>
                  </ShareLinkContainer>
                </>
              )}
            </Section>
          )}
        </TabContent>
      </PanelBody>
    </PanelContainer>
  )
}
